'use client';

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaArrowLeft, FaArrowRight, FaCalendarAlt, FaCheck, FaClock, FaWrench } from "react-icons/fa";
import { fetchAppointments, displayAppointments, createAppointmentApi } from "../../../feature/reducers/appointmentSlice";
import { displayUserById } from "../../../feature/reducers/userSlice";
import { RootState, AppDispatch } from "../../../feature/store/store";
import { IAppointment } from "../../../interface";
import { NotificationService } from "../../../service/NotificationService";

// ───────────── Einstellungen ─────────────

const OPENING_HOURS = "Montag - Samstag, 07:30 - 18:00 Uhr";
const AVAILABLE_TIMES = ["07:30", "09:00", "10:30", "12:00", "13:30", "15:00", "16:30", "18:00"];
// Für diese Leistungen brauchen wir HSN/TSN (aus dem Fahrzeugschein)
const NEEDS_HSN_TSN = ["Ölservice", "Aufbereitung"];

const SERVICES = [
    {
        value: "Ölservice",
        description:
            "Ein regelmäßiger Ölwechsel schützt Ihren Motor vor Verschleiß und erhöht die Lebensdauer Ihres Fahrzeugs. Wir verwenden hochwertiges Motoröl, abgestimmt auf die Anforderungen Ihres Fahrzeugs.",
        image: "/ölwechsel.jpeg",
    },
    {
        value: "Aufbereitung",
        description:
            "Eine umfassende Kontrolle aller sicherheitsrelevanten Komponenten wie Bremsen, Reifen, Lenkung und Beleuchtung – damit Ihr Fahrzeug technisch und optisch in Top-Zustand ist.",
        image: "/aufbreitung.jpg",
    },
    {
        value: "Radwechsel",
        description:
            "Schneller und professioneller Wechsel zwischen Sommer- und Winterrädern. Wir prüfen Ihre Reifen auf Verschleiß und sorgen für die korrekte Montage.",
        image: "/reifen.jpg",
    },
    {
        value: "Reifenmontage",
        description:
            "Neue Reifen fachgerecht auf die Felge montiert und gewuchtet – für optimalen Grip und Sicherheit auf der Straße.",
        image: "/mont.jpg",
    },
];

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// ───────────── Hilfsfunktionen ─────────────

// Format, das das Backend erwartet: 2026-10-15
const toApiDate = (date: Date) =>
    new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

const toGermanDate = (date: Date) =>
    date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const inputClass =
    "mt-2 h-[52px] w-full rounded-[10px] border border-ao-line-strong bg-ao-bg px-4 text-[15px] text-ao-fg placeholder:text-ao-muted focus:border-ao-accent focus:outline-none";
const labelClass = "text-[13px] font-semibold text-ao-muted";
const errorClass = "mt-1.5 text-sm text-red-600 dark:text-red-400";

type Step = 1 | 2 | 3 | "done";

const StepIndicator = ({ step }: { step: Step }) => {
    const current = step === "done" ? 4 : step;
    const labels = ["Leistung", "Termin", "Ihre Daten"];
    return (
        <ol className="grid grid-cols-3 gap-2">
            {labels.map((label, i) => {
                const n = i + 1;
                const done = n < current;
                const active = n === current;
                return (
                    <li key={label} className="flex flex-col gap-2">
                        <span className={`h-1 rounded-full ${n <= current ? "bg-ao-accent" : "bg-ao-line"}`} />
                        <span
                            className={`flex items-center gap-2 text-[13px] font-semibold ${
                                active ? "text-ao-fg" : done ? "text-ao-accent-fg" : "text-ao-muted"
                            }`}
                        >
                            {done ? <FaCheck className="text-xs" /> : <span>{n}.</span>}
                            {label}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
};

// ───────────── Seite ─────────────

const UserCalendar: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { status } = useSelector((state: RootState) => state.appointments);
    const items = useSelector((state: RootState) => displayAppointments(state));

    const [userId, setUserId] = useState<string | null>(null);
    const user = useSelector((state: RootState) => (userId ? displayUserById(state, userId) : undefined));

    const [step, setStep] = useState<Step>(1);
    const [service, setService] = useState<string>("");
    const [expanded, setExpanded] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [month, setMonth] = useState(() => {
        const d = startOfToday();
        d.setDate(1);
        return d;
    });
    const [booked, setBooked] = useState<IAppointment | null>(null);

    useEffect(() => {
        setUserId(localStorage.getItem("userId"));
    }, []);

    useEffect(() => {
        if (status === "idle") dispatch(fetchAppointments());
    }, [status, dispatch]);

    // Belegte Zeiten als "2026-10-15|10:30"
    const takenSlots = useMemo(() => {
        const set = new Set<string>();
        items.forEach((a: IAppointment) => {
            if (a.isBookedOrBlocked && a.date) {
                set.add(`${toApiDate(new Date(a.date))}|${(a.time || "").padStart(5, "0")}`);
            }
        });
        return set;
    }, [items]);

    const today = startOfToday();
    const isDayDisabled = (d: Date) => d < today || d.getDay() === 0; // Vergangenheit & Sonntag

    const isTimeDisabled = (time: string) => {
        if (!selectedDate) return true;
        if (takenSlots.has(`${toApiDate(selectedDate)}|${time}`)) return true;
        const [h, m] = time.split(":").map(Number);
        const slot = new Date(selectedDate);
        slot.setHours(h, m, 0, 0);
        return slot < new Date();
    };

    // Tage des angezeigten Monats (Woche beginnt Montag)
    const calendarDays = useMemo(() => {
        const first = new Date(month);
        const offset = (first.getDay() + 6) % 7;
        const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        const cells: (Date | null)[] = Array(offset).fill(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
        return cells;
    }, [month]);

    const canGoBack = month > new Date(today.getFullYear(), today.getMonth(), 1);
    const changeMonth = (delta: number) =>
        setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

    const needsHsnTsn = (s: string) => NEEDS_HSN_TSN.includes(s);

    const validationSchema = Yup.object({
        firstName: Yup.string().trim().required("Vorname ist erforderlich"),
        lastName: Yup.string().trim().required("Nachname ist erforderlich"),
        email: Yup.string().trim().email("Ungültige E-Mail-Adresse").required("E-Mail ist erforderlich"),
        phone: Yup.string()
            .required("Telefonnummer ist erforderlich")
            .matches(/^[+\d\s/()-]+$/, "Nur Ziffern, Leerzeichen, + und -")
            .test("digits", "Bitte eine gültige Telefonnummer eingeben", (v) => {
                const digits = (v || "").replace(/\D/g, "");
                return digits.length >= 10 && digits.length <= 15;
            }),
        licensePlate: Yup.string().trim().required("Kennzeichen ist erforderlich"),
        // HSN/TSN nur prüfen, wenn sie für die Leistung gebraucht werden
        hsn: needsHsnTsn(service)
            ? Yup.string().trim().required("HSN ist erforderlich").min(3, "Mindestens 3 Zeichen")
            : Yup.string().notRequired(),
        tsn: needsHsnTsn(service)
            ? Yup.string().trim().required("TSN ist erforderlich").min(3, "Mindestens 3 Zeichen")
            : Yup.string().notRequired(),
        comment: Yup.string(),
    });

    const initialValues = {
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phone: (user as any)?.phone || "",
        licensePlate: "",
        hsn: "",
        tsn: "",
        comment: "",
    };

    const handleSubmit = async (values: typeof initialValues) => {
        if (!selectedDate || !selectedTime) return;
        const payload = {
            ...values,
            service,
            date: toApiDate(selectedDate),
            time: selectedTime,
            licensePlate: values.licensePlate.trim().toUpperCase(),
            // Termin mit dem Konto verknüpfen, damit er unter "Meine Buchungen" erscheint
            ...(userId ? { userId } : {}),
        } as IAppointment;

        try {
            await dispatch(createAppointmentApi(payload)).unwrap();
            dispatch(fetchAppointments()); // belegte Zeiten aktualisieren
            setBooked(payload);
            setStep("done");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error: any) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.response?.data?.message || error?.message || "Der Termin konnte nicht gebucht werden.";
            NotificationService.error(message);
            // Zeit war inzwischen vergeben → zurück zur Auswahl
            if (/bereits gebucht|blockiert/i.test(message)) {
                dispatch(fetchAppointments());
                setSelectedTime(null);
                setStep(2);
            }
        }
    };

    const restart = () => {
        setStep(1);
        setService("");
        setSelectedDate(null);
        setSelectedTime(null);
        setBooked(null);
    };

    return (
        <section className="bg-ao-bg text-ao-fg">
            <div className="mx-auto flex max-w-[1200px] flex-col gap-8 px-5 py-10 lg:gap-10 lg:px-20 lg:py-16">

                {/* Kopf */}
                <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
                    <div className="flex flex-col gap-3">
                        <span className="text-xs font-semibold tracking-[0.18em] text-ao-accent-fg lg:text-[13px]">
                            WERKSTATTTERMIN
                        </span>
                        <h1 className="font-display text-4xl font-extrabold uppercase leading-none lg:text-6xl">
                            Termin online buchen
                        </h1>
                    </div>
                    <p className="flex items-center gap-2 text-[15px] text-ao-muted">
                        <FaClock className="text-ao-accent-fg" />
                        {OPENING_HOURS}
                    </p>
                </div>

                <StepIndicator step={step} />

                {/* ───── Schritt 1: Leistung ───── */}
                {step === 1 && (
                    <div className="grid gap-4 md:grid-cols-2 lg:gap-5">
                        {SERVICES.map((s) => {
                            const isOpen = expanded === s.value;
                            return (
                                <article
                                    key={s.value}
                                    className="flex flex-col overflow-hidden rounded-2xl border border-ao-line bg-ao-surface"
                                >
                                    <div className="aspect-[16/9] overflow-hidden bg-ao-surface-2">
                                        <img src={s.image} alt={s.value} className="h-full w-full object-cover" loading="lazy" />
                                    </div>
                                    <div className="flex flex-1 flex-col gap-3 p-6">
                                        <h2 className="font-display text-[28px] font-bold uppercase leading-none">{s.value}</h2>
                                        <p className={`text-[15px] leading-relaxed text-ao-muted ${isOpen ? "" : "line-clamp-2"}`}>
                                            {s.description}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setExpanded(isOpen ? null : s.value)}
                                            className="self-start text-sm font-semibold text-ao-accent-fg hover:underline"
                                        >
                                            {isOpen ? "Weniger anzeigen" : "Mehr lesen"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setService(s.value);
                                                setStep(2);
                                            }}
                                            className="mt-auto flex h-12 items-center justify-center gap-2 rounded-xl bg-ao-accent font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
                                        >
                                            Auswählen <FaArrowRight className="text-sm" />
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {/* ───── Schritt 2: Datum & Uhrzeit ───── */}
                {step === 2 && (
                    <div className="flex flex-col gap-6">
                        <div className="flex items-center justify-between gap-4 rounded-2xl border border-ao-line bg-ao-surface p-4">
                            <span className="flex items-center gap-3 font-semibold">
                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
                                    <FaWrench />
                                </span>
                                {service}
                            </span>
                            <button type="button" onClick={() => setStep(1)} className="text-sm font-semibold text-ao-accent-fg hover:underline">
                                Ändern
                            </button>
                        </div>

                        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
                            {/* Kalender */}
                            <div className="rounded-2xl border border-ao-line bg-ao-surface p-5 lg:p-6">
                                <div className="mb-4 flex items-center justify-between">
                                    <button
                                        type="button"
                                        aria-label="Vorheriger Monat"
                                        disabled={!canGoBack}
                                        onClick={() => changeMonth(-1)}
                                        className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong transition-colors hover:border-ao-accent disabled:opacity-30"
                                    >
                                        <FaArrowLeft className="text-sm" />
                                    </button>
                                    <span className="font-display text-2xl font-bold uppercase">
                                        {month.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label="Nächster Monat"
                                        onClick={() => changeMonth(1)}
                                        className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong transition-colors hover:border-ao-accent"
                                    >
                                        <FaArrowRight className="text-sm" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-7 gap-1.5 text-center">
                                    {WEEKDAYS.map((d) => (
                                        <span key={d} className="pb-2 text-xs font-semibold text-ao-muted">
                                            {d}
                                        </span>
                                    ))}
                                    {calendarDays.map((day, i) => {
                                        if (!day) return <span key={`empty-${i}`} />;
                                        const disabled = isDayDisabled(day);
                                        const selected = selectedDate?.toDateString() === day.toDateString();
                                        const isToday = day.toDateString() === today.toDateString();
                                        return (
                                            <button
                                                key={day.toISOString()}
                                                type="button"
                                                disabled={disabled}
                                                aria-pressed={selected}
                                                aria-label={toGermanDate(day)}
                                                onClick={() => {
                                                    setSelectedDate(day);
                                                    setSelectedTime(null);
                                                }}
                                                className={`flex aspect-square items-center justify-center rounded-[10px] text-[15px] font-semibold transition-colors ${
                                                    selected
                                                        ? "bg-ao-accent text-ao-on-accent"
                                                        : disabled
                                                        ? "cursor-not-allowed text-ao-line-strong"
                                                        : `hover:bg-ao-surface-2 ${isToday ? "border border-ao-accent" : ""}`
                                                }`}
                                            >
                                                {day.getDate()}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-4 text-[13px] text-ao-muted">Sonntags geschlossen.</p>
                            </div>

                            {/* Uhrzeiten */}
                            <div className="flex flex-col gap-4 rounded-2xl border border-ao-line bg-ao-surface p-5 lg:p-6">
                                <span className="font-display text-2xl font-bold uppercase">Uhrzeit</span>
                                {selectedDate ? (
                                    <>
                                        <span className="text-[15px] text-ao-muted">{toGermanDate(selectedDate)}</span>
                                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2">
                                            {AVAILABLE_TIMES.map((time) => {
                                                const disabled = isTimeDisabled(time);
                                                const selected = selectedTime === time;
                                                return (
                                                    <button
                                                        key={time}
                                                        type="button"
                                                        disabled={disabled}
                                                        aria-pressed={selected}
                                                        onClick={() => setSelectedTime(time)}
                                                        className={`h-12 rounded-[10px] border text-[15px] font-bold transition-colors ${
                                                            selected
                                                                ? "border-ao-accent bg-ao-accent text-ao-on-accent"
                                                                : disabled
                                                                ? "cursor-not-allowed border-ao-line text-ao-line-strong line-through"
                                                                : "border-ao-line-strong hover:border-ao-accent"
                                                        }`}
                                                    >
                                                        {time}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {AVAILABLE_TIMES.every(isTimeDisabled) && (
                                            <p className="text-sm text-ao-muted">An diesem Tag ist leider nichts mehr frei.</p>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center text-ao-muted">
                                        <FaCalendarAlt className="text-3xl text-ao-line-strong" />
                                        Bitte zuerst einen Tag im Kalender wählen.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="flex h-12 items-center justify-center gap-2 rounded-xl border border-ao-line-strong px-6 font-semibold transition-colors hover:border-ao-accent"
                            >
                                <FaArrowLeft className="text-sm" /> Zurück
                            </button>
                            <button
                                type="button"
                                disabled={!selectedDate || !selectedTime}
                                onClick={() => setStep(3)}
                                className="flex h-12 items-center justify-center gap-2 rounded-xl bg-ao-accent px-8 font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Weiter <FaArrowRight className="text-sm" />
                            </button>
                        </div>
                    </div>
                )}

                {/* ───── Schritt 3: Ihre Daten ───── */}
                {step === 3 && selectedDate && selectedTime && (
                    <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
                        {/* Zusammenfassung */}
                        <aside className="flex h-fit flex-col gap-4 rounded-2xl border border-ao-line bg-ao-surface p-6 lg:sticky lg:top-28">
                            <span className="font-display text-2xl font-bold uppercase">Ihr Termin</span>
                            <div className="flex flex-col gap-3 text-[15px]">
                                <div className="flex items-center gap-3">
                                    <FaWrench className="text-ao-accent-fg" />
                                    <span className="font-semibold">{service}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCalendarAlt className="text-ao-accent-fg" />
                                    <span className="font-semibold">{toGermanDate(selectedDate)}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaClock className="text-ao-accent-fg" />
                                    <span className="font-semibold">{selectedTime} Uhr</span>
                                </div>
                            </div>
                            <button type="button" onClick={() => setStep(2)} className="self-start text-sm font-semibold text-ao-accent-fg hover:underline">
                                Termin ändern
                            </button>
                        </aside>

                        {/* Formular */}
                        <div className="rounded-2xl border border-ao-line bg-ao-surface p-6 lg:p-8">
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                                enableReinitialize
                            >
                                {({ isSubmitting }) => (
                                    <Form noValidate className="flex flex-col gap-5">
                                        <div className="grid gap-5 sm:grid-cols-2">
                                            <label className={labelClass}>
                                                Vorname
                                                <Field name="firstName" autoComplete="given-name" className={inputClass} />
                                                <ErrorMessage name="firstName" component="p" className={errorClass} />
                                            </label>
                                            <label className={labelClass}>
                                                Nachname
                                                <Field name="lastName" autoComplete="family-name" className={inputClass} />
                                                <ErrorMessage name="lastName" component="p" className={errorClass} />
                                            </label>
                                        </div>
                                        <div className="grid gap-5 sm:grid-cols-2">
                                            <label className={labelClass}>
                                                E-Mail
                                                <Field name="email" type="email" autoComplete="email" className={inputClass} />
                                                <ErrorMessage name="email" component="p" className={errorClass} />
                                            </label>
                                            <label className={labelClass}>
                                                Telefon
                                                <Field name="phone" type="tel" autoComplete="tel" placeholder="0151 23456789" className={inputClass} />
                                                <ErrorMessage name="phone" component="p" className={errorClass} />
                                            </label>
                                        </div>
                                        <label className={labelClass}>
                                            Kennzeichen
                                            <Field name="licensePlate" placeholder="MZ-AB 123" className={`${inputClass} uppercase`} />
                                            <ErrorMessage name="licensePlate" component="p" className={errorClass} />
                                        </label>

                                        {needsHsnTsn(service) && (
                                            <div className="flex flex-col gap-3 rounded-xl bg-ao-surface-2 p-4">
                                                <p className="text-[13px] leading-relaxed text-ao-muted">
                                                    Für {service} brauchen wir HSN und TSN. Sie stehen im Fahrzeugschein unter <strong>2.1</strong> (HSN) und <strong>2.2</strong> (TSN).
                                                </p>
                                                <div className="grid gap-5 sm:grid-cols-2">
                                                    <label className={labelClass}>
                                                        HSN
                                                        <Field name="hsn" placeholder="z. B. 0603" className={inputClass} />
                                                        <ErrorMessage name="hsn" component="p" className={errorClass} />
                                                    </label>
                                                    <label className={labelClass}>
                                                        TSN
                                                        <Field name="tsn" placeholder="z. B. BHE" className={`${inputClass} uppercase`} />
                                                        <ErrorMessage name="tsn" component="p" className={errorClass} />
                                                    </label>
                                                </div>
                                            </div>
                                        )}

                                        <label className={labelClass}>
                                            Kommentar <span className="font-normal">(optional)</span>
                                            <Field
                                                as="textarea"
                                                name="comment"
                                                rows={3}
                                                placeholder="Gibt es etwas, das wir wissen sollten? Bitte keine sensiblen Daten angeben."
                                                className={`${inputClass} h-auto py-3`}
                                            />
                                        </label>

                                        <p className="text-[13px] leading-relaxed text-ao-muted">
                                            Informationen zur Verarbeitung Ihrer Daten finden Sie in unserer{" "}
                                            <Link href="/datenschutz" className="font-semibold text-ao-fg underline">
                                                Datenschutzerklärung
                                            </Link>
                                            .
                                        </p>

                                        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-between">
                                            <button
                                                type="button"
                                                onClick={() => setStep(2)}
                                                className="flex h-12 items-center justify-center gap-2 rounded-xl border border-ao-line-strong px-6 font-semibold transition-colors hover:border-ao-accent"
                                            >
                                                <FaArrowLeft className="text-sm" /> Zurück
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="flex h-12 items-center justify-center gap-2 rounded-xl bg-ao-accent px-8 font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover disabled:opacity-60"
                                            >
                                                {isSubmitting ? "Wird gebucht…" : "Termin verbindlich buchen"}
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                )}

                {/* ───── Fertig ───── */}
                {step === "done" && booked && (
                    <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 rounded-2xl border border-ao-line bg-ao-surface p-8 text-center lg:p-10">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ao-accent text-2xl text-ao-on-accent">
                            <FaCheck />
                        </span>
                        <div className="flex flex-col gap-2">
                            <h2 className="font-display text-4xl font-extrabold uppercase leading-none">Termin gebucht!</h2>
                            <p className="text-[15px] leading-relaxed text-ao-muted">
                                Die Bestätigung ist unterwegs an <strong className="text-ao-fg">{booked.email}</strong>.
                                Dort finden Sie auch den Link zum Stornieren.
                            </p>
                        </div>
                        <dl className="flex w-full flex-col gap-3 rounded-xl bg-ao-surface-2 p-5 text-left text-[15px]">
                            <div className="flex justify-between gap-4"><dt className="text-ao-muted">Leistung</dt><dd className="font-semibold">{booked.service}</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-ao-muted">Datum</dt><dd className="text-right font-semibold">{selectedDate && toGermanDate(selectedDate)}</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-ao-muted">Uhrzeit</dt><dd className="font-semibold">{booked.time} Uhr</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-ao-muted">Kennzeichen</dt><dd className="font-semibold">{booked.licensePlate}</dd></div>
                        </dl>
                        <div className="flex w-full flex-col gap-3 sm:flex-row">
                            <Link
                                href="/"
                                className="flex h-12 flex-1 items-center justify-center rounded-xl bg-ao-accent font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
                            >
                                Zur Startseite
                            </Link>
                            <button
                                type="button"
                                onClick={restart}
                                className="flex h-12 flex-1 items-center justify-center rounded-xl border border-ao-line-strong font-semibold transition-colors hover:border-ao-accent"
                            >
                                Weiteren Termin buchen
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default UserCalendar;
